import React, { useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faXmark } from '@fortawesome/pro-solid-svg-icons';

import './AcceptanceModal.scss';
import Drawer from '../Drawer/Drawer';
import Button, { BUTTON_VARIANTS } from '../UI/Button/Button';
import createLink from '../../../libs/createLink';
import useAcceptance from '../../../hooks/useAcceptance';
import { ACCEPTANCE_TYPES } from '../../../libs/userAcceptance';

// Textes fournis par la production (cf. maquettes Bertrand). TUTOIEMENT assumé : exception validée
// à la règle de vouvoiement du projet, les maquettes ayant été écrites et arbitrées ainsi.
const ACCEPTANCE_CONTENT = {
    [ACCEPTANCE_TYPES.MEDIA]: {
        title: "Avant de publier un média",
        intro: "Publier sur Voissa engage ta responsabilité. Ces règles ne sont pas négociables :",
        rules: [
            "Aucun contenu publié sans le consentement explicite des personnes qui y apparaissent",
            "Aucun contenu dont tu n'es pas l'auteur·rice",
            "Aucun visage ou signe distinctif visible sans certification préalable de ton profil",
        ],
        warning: "Le non-respect de ces règles entraîne un blocage immédiat du compte, pouvant aller jusqu'au bannissement définitif.",
        cta: "Je confirme et je publie",
    },
    [ACCEPTANCE_TYPES.COMMENT]: {
        title: "Avant de publier un commentaire",
        intro: "La bienveillance est une valeur fondamentale de Voissa.",
        text: "Merci de bien lire la bio du profil ou le titre du média avant de commenter, pour t'assurer du type de commentaire attendu par la personne.",
        notice: <>En cas de non-respect, la modération se réserve le droit d'appliquer des <b>sanctions fortes</b>, pouvant aller jusqu'au bannissement.</>,
        cta: "Je confirme et je commente",
    },
};

/**
 * Modale d'acceptation de la charte, présentée au clic sur « Publier » / « Commenter » tant que le
 * membre n'a pas accepté (cf. hooks/useAcceptance.js). Une fois acceptée, elle ne réapparaît
 * jamais : c'est l'engagement pris auprès de la production, et la raison pour laquelle la trace
 * vit côté serveur et non en localStorage.
 *
 * Enregistrée en modale GLOBALE (modalTypes, cf. src/index.js) : elle est déclenchée depuis tous
 * les points de publication, et le filet de sécurité (échec d'une mutation) doit pouvoir l'ouvrir
 * sans connaître le composant appelant.
 *
 * @param {string} type      - ACCEPTANCE_TYPES.MEDIA ou ACCEPTANCE_TYPES.COMMENT
 * @param {function} onAccepted - action mise en attente, rejouée après l'enregistrement
 */
const AcceptanceModal = ({ type = ACCEPTANCE_TYPES.MEDIA, onAccepted, handleClose = () => {} }) => {
    const content = ACCEPTANCE_CONTENT[type] || ACCEPTANCE_CONTENT[ACCEPTANCE_TYPES.MEDIA];
    const { accept, isAccepting } = useAcceptance(type);

    const [checked, setChecked] = useState(false);
    const [error, setError] = useState(null);

    const confirm = async () => {
        if (!checked || isAccepting) return;

        try {
            setError(null);
            // On ATTEND l'API avant de rejouer : le middleware refuserait une publication partie
            // avant que l'acceptation soit enregistrée.
            await accept();
            handleClose();
            onAccepted?.();
        } catch (e) {
            // Rien n'est rejoué : la saisie du membre reste intacte derrière la modale, il peut
            // réessayer sans avoir rien perdu.
            setError("L'enregistrement a échoué. Merci de réessayer.");
        }
    };

    return (
        <Drawer small open={true} handleClose={handleClose}>
            <div className={classNames('acceptance-modal', {
                'acceptance-modal-comment': type === ACCEPTANCE_TYPES.COMMENT,
            })}>
                <div className="acceptance-modal-header">
                    <span className="acceptance-modal-icon">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                    </span>
                    {/* Un <div> et non un <hN> : les balises de titre portent des styles globaux
                        (cf. sass/_title.scss) qui n'ont rien à faire ici, et la modale n'a pas à
                        s'insérer dans la hiérarchie de titres de la page qui l'ouvre. `role` et
                        `aria-level` rendent malgré tout le titre annonçable par un lecteur d'écran. */}
                    <div className="acceptance-modal-title" role="heading" aria-level="2">
                        {content.title}
                    </div>
                </div>

                <p className="acceptance-modal-intro">{content.intro}</p>

                {content.text && <p className="acceptance-modal-text">{content.text}</p>}

                {content.rules && (
                    <ul className="acceptance-modal-rules">
                        {content.rules.map((rule) => (
                            <li key={rule} className="acceptance-modal-rule">
                                <span className="acceptance-modal-rule-icon">
                                    <FontAwesomeIcon icon={faXmark} />
                                </span>
                                <span>{rule}</span>
                            </li>
                        ))}
                    </ul>
                )}

                {content.notice && <p className="acceptance-modal-notice">{content.notice}</p>}

                {content.warning && (
                    <p className="acceptance-modal-warning">
                        <span className="acceptance-modal-warning-icon">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                        </span>
                        <span>{content.warning}</span>
                    </p>
                )}

                <hr className="acceptance-modal-separator" />

                <label className="acceptance-modal-check">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                    />
                    <span>
                        J'ai lu et j'accepte de respecter la{' '}
                        {/* NOUVEL ONGLET délibérément : naviguer vers la charte démonterait la modale
                            (le ModalManager la ferme au changement de route) et ferait perdre
                            l'action mise en attente, donc la saisie en cours. */}
                        <a
                            href={createLink('charter')}
                            target="_blank"
                            rel="noreferrer"
                            className="acceptance-modal-link"
                        >
                            charte de Voissa
                        </a>
                    </span>
                </label>

                {error && <p className="acceptance-modal-error">{error}</p>}

                {/* `aria-disabled` en plus de `disabled` : le composant Button ne répercute pas la
                    prop en attribut HTML sur un <button> (il pose une classe et neutralise le clic
                    dans son handler), donc sans cela un lecteur d'écran annoncerait un bouton
                    actionnable. Ajouté ICI et pas dans Button, qui est partagé par tout le site.
                    Le variant REVERSE donne le CTA inversé — noir en clair, clair en sombre — et
                    gère lui-même son état grisé : la modale n'a aucun style de bouton à écrire. */}
                <Button
                    full
                    variant={BUTTON_VARIANTS.REVERSE}
                    onClick={confirm}
                    disabled={!checked}
                    aria-disabled={!checked}
                    loading={isAccepting}
                >
                    {content.cta}
                </Button>

                <p className="acceptance-modal-footnote">
                    Ce message s'affiche une seule fois, à vie sur ton compte
                </p>
            </div>
        </Drawer>
    );
};

export default AcceptanceModal;
